import classNames from 'classnames';
import _ from 'lodash';
import { colorify } from 'lottie-colorify';
import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { isMobile } from 'react-device-detect';
import { BsFillXCircleFill, BsSearch } from 'react-icons/bs';
import { createUseStyles } from 'react-jss';
import Lottie from 'react-lottie-player';
import VisibilityDetector from 'react-visibility-detector';
import tinycolor from 'tinycolor2';
import { useStorage } from '../../hooks/use-storage';
import { layoutDefaults } from '../../utils/layout-defaults';
import { parseContainerBackgroundShade } from '../../utils/manager/layout-manager';
import { ItemDivider } from '../menu/item-divider';
import noResultData from './no-result.json';
import { InputFilter } from '../input-filter';

const topAnimationEnter =
  'animate__animated animate__fadeInDown animate__faster';
const topAnimationExit = 'animate__animated animate__fadeOutUp animate__faster';
const resultAnimation = 'animate__animated animate__heartBeat';

const animate = 'animate__animated animate__fadeInLeft animate__delay-1s';

function SearchBar() {
  const {
    menuLayout,
    showingModal,
    filter,
    setFilter,
    filterLoading,
    filterCount,
    setFilterLoading,
  } = useStorage();
  const [isEditableSearchVisible, setIsEditableSearchVisible] = useState();
  const [topAnimationClass, setTopAnimationClass] = useState(null);
  const [resultAnimationClass, setResultAnimationClass] = useState(null);
  // const [isFocused, setIsFocused] = useState(false);
  const classes = useStyles({ layout: menuLayout });
  const [value, setValue] = useState('');
  // const { scrollY } = useScroll({ enabled: isFocused });
  const lottieRef = useRef();
  const inputRef = useRef();
  const { t } = useTranslation();

  useEffect(() => {
    if (filterLoading === false) {
      setResultAnimationClass(resultAnimation);
    } else {
      setResultAnimationClass(null);
    }
  }, [filterLoading]);

  useEffect(() => {
    let lAnimationClass;

    if (showingModal) {
      setTopAnimationClass(topAnimationExit);
      return;
    }

    if (isEditableSearchVisible) {
      lAnimationClass = null;
    } else {
      lAnimationClass = topAnimationEnter;
      inputRef.current.blur();

      if ((filterCount ?? 0) === 0) {
        setFilter('');
        setValue('');
      }
    }

    setTopAnimationClass(lAnimationClass);
  }, [isEditableSearchVisible, showingModal]);

  useEffect(() => {
    if (!isEditableSearchVisible && filter !== value) {
      setValue(filter);
    }
  }, [isEditableSearchVisible]);

  useLayoutEffect(() => {
    if (filterLoading === false) {
      _.delay(() => {
        if (lottieRef.current != null) {
          lottieRef.current.loop = true;
          lottieRef.current.play();
        }
      }, 1000);
    }
  }, [filterLoading]);

  const noResultColor = useMemo(
    () =>
      tinycolor(menuLayout?.background?.container_color)
        .darken(6)
        .toHexString() ?? '#ffffff',
    [menuLayout]
  );

  const onChange = (event) => {
    const lValue = event?.target?.value ?? '';

    console.log('onChange', lValue);

    setFilterLoading(true);
    setValue(lValue);
  };

  const onDebounce = (pValue) => {
    setFilter(pValue);
  };

  const onClear = () => {
    setValue('');
    setFilter('');
  };

  const resultText = useMemo(() => {
    const count = filterCount ?? 0;

    if (count === 0) {
      return (
        <span>
          {t('search-bar.nothing-found')}
          <br />
          <h2>{filter}</h2>
        </span>
      );
    }

    return t(`search-bar.showing`, { count });
  }, [filterCount, filter]);

  const onVisibilityChange = ({ visible }) => {
    setIsEditableSearchVisible(visible);
  };

  const onFakeBarClick = () => {
    setIsEditableSearchVisible(true);
    // eslint-disable-next-line no-undef
    window.scrollTo(0, 0);
  };

  return (
    <>
      {isEditableSearchVisible === false && (
        <div
          className={classNames(classes.fakeTopBarContainer, topAnimationClass)}
          onClick={onFakeBarClick}
          aria-hidden="true"
          style={{
            '--animate-delay': `0.1s`,
          }}
        >
          <div className={classes.fakeTopBar}>
            <BsSearch className={classes.fakeSearchIcon} size={12} />
            {_.isEmpty(filter) ? (
              <span className={classes.fakePlaceholder}>
                {t('search-bar.placeholder')}
              </span>
            ) : (
              filter
            )}
            {!_.isEmpty(value) && (
              <BsFillXCircleFill className={classes.fakeClearIcon} size={12} />
            )}
          </div>
        </div>
      )}
      <div
        className={classNames(
          classes.container,
          'animate__animated animate__fadeInUp animate__delay-1s'
        )}
        style={{
          '--animate-delay': `1.0s`,
        }}
      >
        <VisibilityDetector
          onVisibilityChange={onVisibilityChange}
          className={classes.inputContainer}
        >
          <InputFilter
            className={classes.input}
            inputRef={inputRef}
            value={value}
            onChange={onChange}
            onDebounce={onDebounce}
            placeholder={t('search-bar.placeholder')}
            maxLength={40}
            loading={filterLoading}
          />
        </VisibilityDetector>

        {filterCount !== 0 && (
          <div className={classNames(classes.resultText, resultAnimationClass)}>
            {resultText}
          </div>
        )}

        <ItemDivider
          className={animate}
          style={{ '--animate-delay': '1.3s' }}
        />

        {filterCount === 0 && !_.isEmpty(filter) && (
          <div
            onClick={onClear}
            aria-hidden="true"
            className={classNames(
              classes.noResultContainer,
              'animate__animated animate__fadeIn'
            )}
          >
            <Lottie
              ref={lottieRef}
              play={false}
              loop
              speed={0.8}
              animationData={colorify(
                [
                  noResultColor,
                  noResultColor,
                  noResultColor,
                  noResultColor,
                  '',
                  noResultColor,
                  noResultColor,
                  '',
                  '',
                  noResultColor,
                  noResultColor,
                  noResultColor,
                  noResultColor,
                ],
                noResultData
              )}
              className={classes.noResultLottie}
            />
            {!filterLoading && (
              <div
                className={classNames(
                  classes.resultText,
                  resultAnimationClass,
                  classes.offsetResult
                )}
              >
                {resultText}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

const useStyles = createUseStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',

    marginTop: layoutDefaults.bigVerticalMargin,
    marginBottom: layoutDefaults.biggerVerticalMargin - 4,
    marginLeft: layoutDefaults.biggerHorizontalMargin,
    marginRight: layoutDefaults.biggerHorizontalMargin,
    // border: '1px solid white',
  },
  input: {
    borderRadius: 100,
  },
  fakeTopBarContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'fixed',
    width: '100%',
    maxWidth: isMobile ? null : layoutDefaults.maxScreenWidth,
    fontWeight: '200',
    top: 0,
    zIndex: 10,
    // border: '1px solid white',
  },
  fakeTopBar: {
    display: 'flex',
    justifyContent: 'center',
    color: ({ layout }) => layout?.text?.default_color,
    backgroundColor: ({ layout }) => parseContainerBackgroundShade(layout, 10),
    maxWidth: '60%',
    opacity: 0.86,
    fontSize: layoutDefaults.smallerFontSize,
    marginTop: layoutDefaults.smallVerticalMargin,
    paddingTop: layoutDefaults.smallerVerticalMargin,
    paddingBottom: layoutDefaults.smallerVerticalMargin,
    paddingLeft: layoutDefaults.horizontalMargin - 2,
    paddingRight: layoutDefaults.smallHorizontalMargin,
    borderRadius: 100,
    cursor: 'pointer',
    // fontWeight: 'bold',

    boxShadow: `0px -2px 4px #44444466`,
    border: ({ layout }) =>
      `2px solid ${tinycolor(layout?.text?.default_color)
        .setAlpha(0.45)
        .toHex8String()}`,
  },

  inputContainer: {
    width: '100%',
  },
  fakePlaceholder: {
    fontSize: layoutDefaults.smallestFontSize,
    // fontWeight: 'normal',
    textTransform: 'uppercase',
    marginRight: layoutDefaults.smallHorizontalMargin,

    // border: '1px solid red',
  },
  fakeSearchIcon: {
    fontWeight: 'bold',
    alignSelf: 'center',
    marginRight: layoutDefaults.smallHorizontalMargin,
  },
  fakeClearIcon: {
    fontWeight: 'bold',
    // color: '#aaa',
    color: ({ layout }) =>
      tinycolor(layout?.background?.container_color)
        .lighten(35)
        .setAlpha(0.8)
        .toHex8String(),
    alignSelf: 'center',
    marginLeft: layoutDefaults.smallHorizontalMargin,
  },
  resultText: {
    fontSize: layoutDefaults.smallFontSize,
    opacity: layoutDefaults.opacity,
    textAlign: 'center',
    alignSelf: 'center',

    // border: '1px solid white',
  },
  noResultContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignContent: 'center',

    cursor: 'pointer',
    marginTop: '0.3vh',

    // border: '1px solid red',
  },
  noResultLottie: {
    alignSelf: 'center',
    width: 200,
    opacity: 0.85,
    marginLeft: layoutDefaults.biggerHorizontalMargin,
    marginBottom: layoutDefaults.verticalMargin,
    zIndex: 5,

    // border: '1px solid blue',
  },
  offsetResult: {
    marginTop: -40,
    fontSize: layoutDefaults.bigFontSize,
  },
});

export { SearchBar };
